import React, { useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import PostData from "../../services/PostData.js";
import ProductFormDialog from "../Products/ProductFormDialog.jsx";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";

const formatDate = (dateString) => {
    if (!dateString) return '';
    // Pass through calendar strings from CSV as-is (server parses as America/New_York)
    if (typeof dateString === 'string') {
        const raw = dateString.trim();
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) return raw;
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
        // Normalize mm-dd-YYYY → mm/dd/YYYY for the confirm UI
        const dashed = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (dashed) return `${dashed[1]}/${dashed[2]}/${dashed[3]}`;
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    // Default "today" for missing invoice date — use Eastern calendar day
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return `${get('month')}/${get('day')}/${get('year')}`;
};

const isMacBidSupplier = (name) => {
    const p = String(name || "")
        .toLowerCase()
        .replace(/_/g, ".")
        .replace(/\s+/g, " ")
        .trim();
    if (!p) return false;
    return (
        p === "mac.bid" ||
        p === "mac bid" ||
        p.startsWith("mac.bid") ||
        p.startsWith("mac bid") ||
        p.includes("mac.bid")
    );
};

function createSeedsForMatch({ productField, matchValue, description }) {
    const seeds = {
        itemName: String(description || "").trim(),
    };
    const value = String(matchValue || "").trim();
    if (!value) return seeds;

    switch (productField) {
        case "asin":
            seeds.asin = value;
            seeds.amazonUrl = `https://www.amazon.com/dp/${value}`;
            break;
        case "upc":
            seeds.upc = value;
            break;
        case "gtin":
            seeds.gtin = value;
            break;
        case "itemName":
            seeds.itemName = value;
            break;
        case "walmartLinkedSku":
            seeds.walmartLinkedSku = value;
            break;
        case "ebayLinkedSku":
            seeds.ebayLinkedSku = value;
            break;
        default:
            break;
    }
    return seeds;
}

export default function SpreadsheetResponse({ responseData, closeDialog }) {
    const [submitLoad, setSubmitLoad] = useState(false)
    const [successUpload, setSuccessUpload] = useState(false)
    const [linking, setLinking] = useState(false)
    const [unmatchedPrompt, setUnmatchedPrompt] = useState({
        open: false,
        fieldLabel: "Product field",
        productField: "",
        unmatched: [],
    })
    const [createDialog, setCreateDialog] = useState({
        open: false,
        group: null,
    })
    /** Kept across ProductFormDialog close so link-after-create still has the group. */
    const pendingUnmatchedGroupRef = useRef(null)
    const [snack, setSnack] = useState({ open: false, color: "", message: "" })
    const [inputVals, setInputVals] = useState([
        { field: "Invoice ID", data: responseData?.invoiceId ?? '', name: "invoice_id" },
        {
            field: "Supplier Name",
            data: responseData?.supplierName || responseData?.supplierDisplayName || '',
            name: "supplier_name",
        },
        {
            field: "Date",
            data: responseData?.invoiceDate
                ? formatDate(responseData.invoiceDate)
                : formatDate(new Date()),
            name: "invoice_date",
        },
    ])

    const [inputItems, setInputItems] = useState(responseData?.invoiceData?.map(item => ({
        description: item?.description ?? '',
        item_number: item?.item_number ?? '',
        qty: Number(item?.qty) || 1,
        price: Number(item?.price) || 0,
        auctionLink: item?.auctionLink ?? item?.auction_link ?? '',
        macItemSubId: item?.macItemSubId ?? '',
        matchValue: item?.matchValue ?? '',
    })) || [])

    const supplierNameLive =
        inputVals.find((v) => v.name === "supplier_name")?.data
        || responseData?.supplierName
        || responseData?.supplierDisplayName
        || '';
    const showMacItemSubId = isMacBidSupplier(supplierNameLive);
    const liveInvoiceId = String(
        inputVals.find((v) => v.name === "invoice_id")?.data
            ?? responseData?.invoiceId
            ?? ""
    ).trim();
    const initialInvoiceId = String(responseData?.invoiceId || "").trim();
    const duplicateStillActive =
        Boolean(responseData?.invoiceAlreadyExists) &&
        liveInvoiceId === initialInvoiceId;
    const macDuplicate = isMacBidSupplier(supplierNameLive);
    const existingSupplierLabel =
        responseData?.existingInvoiceSupplier
        || responseData?.supplierDisplayName
        || responseData?.supplierName
        || "MAC.BID";

    const unmatchedCount = useMemo(
        () => (unmatchedPrompt.unmatched || []).reduce((sum, row) => sum + (row.count || 0), 0),
        [unmatchedPrompt.unmatched]
    );

    const createSeeds = useMemo(() => {
        if (!createDialog.group) return null;
        return createSeedsForMatch({
            productField: unmatchedPrompt.productField,
            matchValue: createDialog.group.matchValue,
            description: createDialog.group.description,
        });
    }, [createDialog.group, unmatchedPrompt.productField]);

    const handleInputChanges = (e, fieldName) => {
        const { value } = e.target;
        setInputVals(prevVals => prevVals.map(val =>
            val.name === fieldName ? { ...val, data: value } : val
        ));
    }

    const handleItemInputChanges = (e, index, fieldName) => {
        const { value } = e.target;
        setInputItems(prevItems => {
            const newItems = [...prevItems];
            newItems[index] = { ...newItems[index], [fieldName]: value };
            return newItems;
        });
    };

    const finishSuccess = () => {
        setSuccessUpload(true)
        setTimeout(() => closeDialog("Success"), 800)
    }

    const handleSubmit = async () => {
        if (duplicateStillActive) {
            setSnack({
                open: true,
                color: "warning",
                message: macDuplicate
                    ? `MAC invoice ${liveInvoiceId || ""} already exists under ${existingSupplierLabel}. Nothing was saved.`
                    : "This invoice number already exists. Nothing was saved.",
            });
            return;
        }
        setSubmitLoad(true)
        const itemsForSubmit = inputItems.map((item) => {
            const payload = {
                description: item.description,
                item_number: item.item_number,
                qty: item.qty,
                price: item.price,
                auctionLink: String(item.auctionLink || "").trim(),
                // Prefer dedicated match column; fall back to item # when they share a mapping.
                matchValue: String(item.matchValue || item.item_number || "").trim(),
            };
            if (showMacItemSubId) {
                const macItemSubId = String(item.macItemSubId || "").trim();
                if (macItemSubId) payload.macItemSubId = macItemSubId;
            }
            return payload;
        });
        const submitVal = {
            headers: inputVals,
            items: itemsForSubmit,
            configId: responseData?.configId,
            productAutoMatch: responseData?.productAutoMatch,
        }
        const { success, error, data } = await PostData(submitVal, "broylesllc/confirm-csv-invoice-data", null)
        setSubmitLoad(false)

        if (error?.response?.status === 409) {
            const data = error?.response?.data || {}
            if (data.code === "invoice_exists") {
                const under = data.existingInvoiceSupplier
                    ? ` under ${data.existingInvoiceSupplier}`
                    : "";
                setSnack({
                    open: true,
                    color: "warning",
                    message: macDuplicate
                        ? `MAC invoice already exists${under}. Nothing was saved to MongoDB.`
                        : "This invoice number already exists. Nothing was saved.",
                });
                setSuccessUpload(true)
                setTimeout(() => closeDialog("Exists"), 800)
                return
            }
            setSuccessUpload(true)
            setTimeout(() => closeDialog("Exists"), 800)
            return
        }
        if (error) {
            setSnack({
                open: true,
                color: "error",
                message:
                    error?.response?.data?.message
                    || "Could not save invoice.",
            })
            return
        }
        if (!success) return

        const unmatched = Array.isArray(data?.unmatched) ? data.unmatched : []
        if (unmatched.length > 0) {
            setUnmatchedPrompt({
                open: true,
                fieldLabel:
                    data?.productAutoMatch?.fieldLabel
                    || responseData?.productAutoMatch?.fieldLabel
                    || "Product field",
                productField:
                    data?.productAutoMatch?.productField
                    || responseData?.productAutoMatch?.productField
                    || "",
                unmatched,
            })
            return
        }

        finishSuccess()
    }

    const handleProductCreated = async (product) => {
        const group = pendingUnmatchedGroupRef.current || createDialog.group
        const productId = product?._id || product?.id
        if (!group || !productId) {
            setSnack({
                open: true,
                color: "error",
                message: "Product was created but could not be linked.",
            })
            throw new Error("Product was created but could not be linked.")
        }

        setLinking(true)
        try {
            const { success, error } = await PostData(
                {
                    productId,
                    catalogItemIds: group.catalogItemIds || [],
                },
                "broylesllc/catalog-items/link-product",
                null
            )

            if (!success) {
                const message =
                    error?.response?.data?.message
                    || "Could not link catalog items to the new product."
                setSnack({ open: true, color: "error", message })
                throw new Error(message)
            }

            pendingUnmatchedGroupRef.current = null
            setCreateDialog({ open: false, group: null })
            setUnmatchedPrompt((prev) => {
                const nextUnmatched = (prev.unmatched || []).filter(
                    (row) => row.matchValue !== group.matchValue
                )
                if (!nextUnmatched.length) {
                    setTimeout(() => {
                        setUnmatchedPrompt({
                            open: false,
                            fieldLabel: prev.fieldLabel,
                            productField: prev.productField,
                            unmatched: [],
                        })
                        finishSuccess()
                    }, 0)
                    return {
                        ...prev,
                        open: false,
                        unmatched: [],
                    }
                }
                // Stay on unmatched dialog until every group is resolved.
                return { ...prev, open: true, unmatched: nextUnmatched }
            })
        } finally {
            setLinking(false)
        }
    }

    const openCreateForGroup = (row) => {
        pendingUnmatchedGroupRef.current = row
        setCreateDialog({ open: true, group: row })
    }

    const handleClose = () => closeDialog()

    return (
        <div style={{
            height: '80vh', display: 'flex',
            flexDirection: 'column', minWidth: '550px', overflowX: "hidden"
        }}>
            <div className='row' style={{
                width: "100%", padding: "20px 0 10px 10px", position: "sticky",
                top: 0, zIndex: 100, background: "white",
                borderBottom: 'solid lightgray', margin: 0
            }}>
                <div className='col-9'>
                    <h4 style={{ marginBottom: "0" }}>Invoice Confirmation: </h4>
                </div>
                <div>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', width: '40px', height: '40px', top: '10px', right: '30px',
                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                            backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                </div>
            </div>
            <div style={{ padding: '0 10px' }}>
                {duplicateStillActive && (
                    <Alert severity="warning" sx={{ mt: 1.5, mb: 1 }}>
                        {macDuplicate ? (
                            <>
                                MAC invoice <strong>{liveInvoiceId || "—"}</strong> already exists
                                {responseData?.existingInvoiceSupplier
                                    ? <> under <strong>{responseData.existingInvoiceSupplier}</strong></>
                                    : null}
                                . Duplicate MAC invoice numbers are not allowed across any MAC.BID
                                location. Submit is blocked — nothing will be written to MongoDB.
                            </>
                        ) : (
                            <>
                                Invoice {liveInvoiceId || "—"} already exists for{" "}
                                {existingSupplierLabel}. Submit is blocked unless you change the
                                invoice number.
                            </>
                        )}
                    </Alert>
                )}
                <div>
                    {inputVals.map((val, ind) =>
                        <div className='row m-0' key={ind}>
                            <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>{val.field}</label>
                            <TextField
                                size="small"
                                name={val.name}
                                value={val.data}
                                onChange={(e) => handleInputChanges(e, val.name)}
                                sx={{ width: '100%' }} />
                        </div>
                    )}
                </div>
                <div>
                    {inputItems.map((item, itemIndex) => (
                        <div key={itemIndex} style={{ border: '1px solid #e0e0e0', padding: '10px', marginBottom: '10px', marginTop: '10px', borderRadius: '5px' }}>
                            <h5 style={{ marginBottom: '10px' }}>Item {itemIndex + 1}</h5>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Name</label>
                                <TextField
                                    size="small"
                                    name='description'
                                    value={item.description}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'description')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Item Number</label>
                                <TextField
                                    size="small"
                                    name='item_number'
                                    value={item.item_number}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'item_number')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            {showMacItemSubId && (
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                        MAC Item Sub ID
                                    </label>
                                    <TextField
                                        size="small"
                                        name='macItemSubId'
                                        value={item.macItemSubId}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'macItemSubId')}
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                            )}
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Auction Link</label>
                                <TextField
                                    size="small"
                                    name='auctionLink'
                                    type='url'
                                    placeholder='https://'
                                    value={item.auctionLink}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'auctionLink')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Quantity</label>
                                <TextField
                                    size="small"
                                    name='qty'
                                    value={item.qty}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'qty')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Unit Price</label>
                                <TextField
                                    size="small"
                                    name='price'
                                    value={item.price}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'price')}
                                    sx={{ width: '100%' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">$</InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='row m-0'
                style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', padding: '10px 10px', borderTop: '1px solid #e0e0e0', justifyContent: 'end' }}>
                {successUpload ?
                    <button className='submit'
                        style={{ width: '205px', background: '#049e04', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Success</span> <CheckOutlinedIcon fontSize="small" sx={{ color: 'white', marginLeft: '10px' }} />
                    </button>
                    :
                    (!submitLoad ? <button
                        className='submit'
                        type='submit'
                        onClick={handleSubmit}
                        disabled={duplicateStillActive}
                        style={{
                            width: '205px',
                            height: '35px',
                            opacity: duplicateStillActive ? 0.55 : 1,
                            cursor: duplicateStillActive ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {duplicateStillActive ? 'Duplicate — blocked' : 'Submit'}
                    </button> :
                        <button className='submit' type='submit'
                            style={{ width: '205px', height: '35px' }}>
                            <CircularProgress size={20} sx={{ color: 'white', marginTop: '5px' }} />
                        </button>)
                }
            </div>

            <Dialog
                open={unmatchedPrompt.open}
                onClose={() => {}}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {unmatchedCount} item{unmatchedCount === 1 ? "" : "s"} were unable to match on {unmatchedPrompt.fieldLabel}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1.5, fontSize: 14, color: "#334155" }}>
                        Create a Product for each unmatched value. Saving links those catalog items and returns here until none remain.
                    </Typography>
                    <div
                        style={{
                            maxHeight: 320,
                            overflow: "auto",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "8px 12px",
                        }}
                    >
                        {(unmatchedPrompt.unmatched || []).map((row) => (
                            <div
                                key={row.matchValue}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 12,
                                    padding: "10px 0",
                                    borderBottom: "1px solid #f1f5f9",
                                }}
                            >
                                <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
                                    <strong style={{ fontSize: 13 }}>{row.matchValue}</strong>
                                    {row.count > 1 ? (
                                        <span style={{ color: "#64748b", fontSize: 12 }}> · {row.count} units</span>
                                    ) : null}
                                    {row.description ? (
                                        <div
                                            style={{
                                                color: "#64748b",
                                                marginTop: 2,
                                                fontSize: 13,
                                                lineHeight: 1.35,
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {row.description}
                                        </div>
                                    ) : null}
                                </div>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={linking}
                                    onClick={() => openCreateForGroup(row)}
                                    sx={{
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                        flex: "0 0 auto",
                                        flexShrink: 0,
                                        mt: 0.25,
                                    }}
                                >
                                    Create Product
                                </Button>
                            </div>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: "auto", pl: 1 }}>
                        Resolve all unmatched items to finish.
                    </Typography>
                </DialogActions>
            </Dialog>

            <ProductFormDialog
                open={createDialog.open}
                createSeeds={createSeeds}
                onClose={() =>
                    setCreateDialog((prev) => ({ ...prev, open: false }))
                }
                onSaved={handleProductCreated}
                onNotify={({ color, message }) =>
                    setSnack({ open: true, color, message })
                }
            />

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}
        </div>
    )
}
